import { useEffect, useState } from "react";
import { CloseIcon } from "../../../components/Icons";
import { Button } from "../../../components/ui/button";
import type { TemplatePreset } from "../../../hooks";
import { useTemplatePresetMutations } from "../../../hooks";

interface TemplatePresetFormDialogProps {
  open: boolean;
  /** null なら新規作成 */
  preset: TemplatePreset | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function TemplatePresetFormDialog({
  open,
  preset,
  onOpenChange,
  onSaved,
}: TemplatePresetFormDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const { createTemplatePreset, updateTemplatePreset, loading, error } =
    useTemplatePresetMutations();

  useEffect(() => {
    if (!open) return;
    setName(preset?.name ?? "");
    setDescription(preset?.description ?? "");
  }, [open, preset]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    // 空文字で送る。null だと Repository の部分更新が更新対象から外し、メモを消せない
    const trimmedDescription = description.trim();
    const saved = preset
      ? await updateTemplatePreset(
          preset.templatePresetId,
          trimmedName,
          trimmedDescription,
        )
      : await createTemplatePreset(trimmedName, trimmedDescription);
    if (!saved) return;
    onSaved();
    onOpenChange(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="w-[90%] max-w-[520px] rounded-xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-bold text-lg">
            {preset
              ? "テンプレートスクリプトを編集"
              : "テンプレートスクリプトを作成"}
          </h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer border-none bg-none p-1"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label
              htmlFor="template-preset-name"
              className="mb-1 block font-medium text-sm"
            >
              名前
            </label>
            <input
              id="template-preset-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="中間インタビュー 30分"
              className="w-full rounded border border-[var(--color-border-default)] p-2 text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="template-preset-description"
              className="mb-1 block font-medium text-sm"
            >
              メモ
            </label>
            <input
              id="template-preset-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="どのタームで使うかの補足"
              className="w-full rounded border border-[var(--color-border-default)] p-2 text-sm"
            />
          </div>
          {error && (
            <p className="text-[var(--color-text-danger)] text-sm">{error}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              キャンセル
            </Button>
            <Button type="submit" disabled={loading || !name.trim()}>
              {loading ? "保存中..." : "保存"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
